import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Mail, Lock, Eye, EyeOff, User, Info } from 'lucide-react';
import { Button, Input, Card } from '@/components/ui';
import { useAuthStore } from '@/stores';
import { useToast } from '@/components/ui/Toast';

const PASSWORD_REQUIREMENTS = [
  'At least 12 characters',
  'One uppercase letter (A-Z)',
  'One lowercase letter (a-z)',
  'One number (0-9)',
  'One special character (!@#$%^&*...)',
];

function PasswordInfoIcon() {
  return (
    <div className="relative group inline-block ml-1">
      <Info className="h-4 w-4 text-gray-400 cursor-help" />
      <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50">
        <div className="bg-gray-900 text-white text-xs rounded-lg py-2 px-3 shadow-lg whitespace-nowrap">
          <p className="font-semibold mb-1">Password requirements:</p>
          <ul className="space-y-0.5">
            {PASSWORD_REQUIREMENTS.map((req, i) => (
              <li key={i}>• {req}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

interface SignupFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

export function SignupPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { signup, loading, clearError } = useAuthStore();
  const toast = useToast();
  const [showPassword, setShowPassword] = useState(false);

  // Preserve redirect parameter (e.g., from /invite/{token})
  const redirectParam = searchParams.get('redirect');
  // Get invited email if coming from invitation flow
  const invitedEmail = searchParams.get('email');
  const isInviteFlow = !!invitedEmail && redirectParam?.startsWith('/invite/');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignupFormData>({
    defaultValues: {
      email: invitedEmail || '',
    },
  });

  const password = watch('password');

  const onSubmit = async (data: SignupFormData) => {
    try {
      clearError();
      const result = await signup(data.email, data.password, data.name);
      toast.success('Account created! Please set up two-factor authentication.');
      // MFA is mandatory - redirect to setup, preserving any redirect parameter
      const mfaUrl = redirectParam ? `/mfa-setup?redirect=${encodeURIComponent(redirectParam)}` : '/mfa-setup';
      if (result.mfaSetupRequired) {
        navigate(mfaUrl);
      } else {
        navigate(redirectParam || '/');
      }
    } catch {
      toast.error('Failed to create account');
    }
  };

  return (
    <Card padding="lg">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Create an account</h2>
        <p className="text-gray-600 mt-1">Start your productivity journey</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Full name"
          type="text"
          placeholder="John Doe"
          autoComplete="name"
          leftIcon={<User className="h-4 w-4" />}
          error={errors.name?.message}
          {...register('name', {
            required: 'Name is required',
            minLength: {
              value: 2,
              message: 'Name must be at least 2 characters',
            },
          })}
        />

        <div>
          <Input
            label="Email"
            type="email"
            placeholder="you@example.com"
            autoComplete="email"
            leftIcon={<Mail className="h-4 w-4" />}
            error={errors.email?.message}
            disabled={isInviteFlow}
            className={isInviteFlow ? 'bg-gray-100' : ''}
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: 'Invalid email address',
              },
            })}
          />
          {isInviteFlow && (
            <p className="text-xs text-gray-500 mt-1">
              This email is locked to match your invitation
            </p>
          )}
        </div>

        <Input
          label={
            <span className="flex items-center">
              Password
              <PasswordInfoIcon />
            </span>
          }
          type={showPassword ? 'text' : 'password'}
          placeholder="••••••••••••"
          autoComplete="new-password"
          leftIcon={<Lock className="h-4 w-4" />}
          rightIcon={
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          }
          error={errors.password?.message}
          {...register('password', {
            required: 'Password is required',
            minLength: {
              value: 12,
              message: 'Password must be at least 12 characters',
            },
            pattern: {
              value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?])/,
              message: 'Password must contain uppercase, lowercase, number, and special character',
            },
          })}
        />

        <Input
          label="Confirm password"
          type={showPassword ? 'text' : 'password'}
          placeholder="••••••••"
          autoComplete="new-password"
          leftIcon={<Lock className="h-4 w-4" />}
          error={errors.confirmPassword?.message}
          {...register('confirmPassword', {
            required: 'Please confirm your password',
            validate: (value) => value === password || 'Passwords do not match',
          })}
        />

        <Button type="submit" className="w-full" loading={loading}>
          Create account
        </Button>
      </form>

      <p className="text-center text-sm text-gray-600 mt-6">
        Already have an account?{' '}
        <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium">
          Sign in
        </Link>
      </p>
    </Card>
  );
}
